/**
 * Typed selector hooks for the portfolio domain.
 *
 * Components import these instead of reaching into the raw store. This keeps
 * type narrowing centralised and makes it easy to refactor the snapshot
 * shape later without touching every consumer.
 *
 * Snapshot shape (from docs/ARCHITECTURE.md §3.1):
 *
 *   { as_of, mode, cash: {cash_balance, available, used_margin, stale},
 *     pnl: {realised, unrealised, gross, brokerage, net, today_pct},
 *     exposure: {by_index, total_open_positions},
 *     drawdown: {peak, current_drawdown_pct} }
 */
import { useStateStore } from "@/state/store";

export interface PortfolioCash {
  cash_balance: number;
  available:    number;
  used_margin:  number;
  stale:        boolean;
}

export interface PortfolioPnl {
  realised:   number;
  unrealised: number;
  gross:      number;
  brokerage:  number;
  net:        number;
  today_pct:  number;
}

export interface PortfolioExposure {
  by_index: Record<string, { lots: number; notional: number }>;
  total_open_positions: number;
}

export interface PortfolioDrawdown {
  peak:                 number;
  current_drawdown_pct: number;
}

export interface PortfolioSnapshot {
  as_of:    string;
  mode:     "paper" | "live";
  cash:     PortfolioCash;
  pnl:      PortfolioPnl;
  exposure: PortfolioExposure;
  drawdown: PortfolioDrawdown;
  _seq?:    number;
}

/** Returns the full portfolio snapshot or null when not yet hydrated. */
export function usePortfolio(): PortfolioSnapshot | null {
  return useStateStore((s) => s.portfolio.snapshot as PortfolioSnapshot | null);
}

/** Cash subsection. */
export function usePortfolioCash(): PortfolioCash | null {
  return useStateStore((s) => {
    const snap = s.portfolio.snapshot as PortfolioSnapshot | null;
    return snap?.cash ?? null;
  });
}

/** P&L subsection. */
export function usePortfolioPnl(): PortfolioPnl | null {
  return useStateStore((s) => {
    const snap = s.portfolio.snapshot as PortfolioSnapshot | null;
    return snap?.pnl ?? null;
  });
}

/** True when the snapshot is known to be stale (WS disconnected or seq gap). */
export function usePortfolioIsStale(): boolean {
  return useStateStore((s) => s.portfolio.isStale);
}

/** Last-update timestamp (Date.now() value), 0 if never. */
export function usePortfolioLastUpdated(): number {
  return useStateStore((s) => s.portfolio.lastUpdatedAt);
}
